import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  Bell, 
  Check, 
  CheckCheck,
  AlertTriangle,
  Package,
  X,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const NotificationBell = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [contagem, setContagem] = useState(0);

  const fetchContagem = useCallback(async () => {
    try {
      const response = await api.get('/notificacoes/contagem');
      setContagem(response.data.nao_lidas || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  }, [api]);

  const fetchNotificacoes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/notificacoes?limite=20');
      setNotificacoes(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Initial fetch and polling
  useEffect(() => {
    // Ao abrir o sistema: pendências de 1º atendimento ainda abertas voltam ao sino mesmo se marcadas como lidas
    api.post('/primeiros-atendimentos/notificacoes/reabrir').catch(() => {}).finally(fetchContagem);
    
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchContagem, 60000);
    return () => clearInterval(interval);
  }, [api, fetchContagem]);

  // Fetch full list when popover opens
  useEffect(() => {
    if (open) {
      fetchNotificacoes();
    }
  }, [open, fetchNotificacoes]);

  const handleMarcarLida = async (notificacaoId) => {
    try {
      await api.put(`/notificacoes/${notificacaoId}/marcar-lida`);
      setNotificacoes(prev => 
        prev.map(n => n.id === notificacaoId ? { ...n, lido_em: new Date().toISOString() } : n)
      );
      setContagem(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const handleMarcarTodasLidas = async () => {
    try {
      await api.put('/notificacoes/marcar-todas-lidas');
      setNotificacoes(prev => prev.map(n => ({ ...n, lido_em: new Date().toISOString() })));
      setContagem(0);
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações');
    }
  };

  const handleVerItem = (notificacao) => {
    if (notificacao.tipo === 'PRIMEIRO_ATENDIMENTO') {
      setOpen(false);
      const d = notificacao.dados_extras || {};
      navigate(d.acao === 'lancar_guia' ? '/guias' : `/primeiros-atendimentos${d.internacao_id ? `?internacao=${d.internacao_id}` : ''}`);
      return;
    }
    if (notificacao.item_id) {
      setOpen(false);
      navigate(`/estoque?tab=itens&item=${notificacao.item_id}`);
    }
  };

  const getIcon = (tipo, severidade) => {
    if (tipo === 'ESTOQUE_CRITICO') {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
    if (tipo === 'VALIDADE_VENCIDA') {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
    if (tipo === 'VALIDADE_PROXIMA') {
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
    return <Package className="h-4 w-4 text-blue-500" />;
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = (now - date) / 1000; // seconds
      
      if (diff < 60) return 'Agora';
      if (diff < 3600) return `${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
      return date.toLocaleDateString('pt-BR');
    } catch {
      return '';
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="btn-notificacoes"
        >
          <Bell className="h-5 w-5" />
          {contagem > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
              {contagem > 99 ? '99+' : contagem}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notificações</h3>
          {contagem > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarcarTodasLidas}
              className="text-xs gap-1"
            >
              <CheckCheck className="h-3 w-3" />
              Marcar todas como lidas
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : notificacoes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">Nenhuma notificação</p>
            </div>
          ) : (
            <div className="divide-y">
              {notificacoes.map((notificacao) => (
                <div
                  key={notificacao.id}
                  className={`p-4 hover:bg-muted/50 transition-colors ${
                    !notificacao.lido_em ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getIcon(notificacao.tipo, notificacao.severidade)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium break-words min-w-0 ${!notificacao.lido_em ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notificacao.titulo}
                        </p>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatTimeAgo(notificacao.criado_em)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">
                        {notificacao.mensagem}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {(notificacao.item_id || notificacao.tipo === 'PRIMEIRO_ATENDIMENTO') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs gap-1"
                            data-testid={`notif-acao-${notificacao.id}`}
                            onClick={() => handleVerItem(notificacao)}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {notificacao.tipo === 'PRIMEIRO_ATENDIMENTO' ? ({ lancar_guia: 'Lançar guia', confirmar: 'Confirmar Primeira Avaliação Realizada', agendar: 'Agendar Primeira Avaliação', decidir: 'Decidir acompanhamento', atribuir: 'Atribuir paciente' }[(notificacao.dados_extras || {}).acao] || 'Abrir paciente') : 'Ver item'}
                          </Button>
                        )}
                        {!notificacao.lido_em && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={() => handleMarcarLida(notificacao.id)}
                          >
                            <Check className="h-3 w-3" />
                            Marcar como lida
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <div className="p-3 border-t bg-muted/50">
          <Button
            variant="ghost"
            className="w-full text-sm"
            onClick={() => {
              setOpen(false);
              navigate('/configuracoes?tab=notificacoes');
            }}
          >
            Configurar notificações
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
